/**
 * Global console error buffer for SupportWidget bug-report attachments.
 *
 * Capture starts as soon as `initConsoleErrorBuffer()` is called from
 * `src/main.tsx` (before React mounts) and runs for the full lifetime of
 * the page. The buffer is bounded to the last MAX_ERRORS entries to avoid
 * memory bloat over long sessions.
 *
 * Sources captured:
 *   - `console.error` calls (patched in-place)
 *   - `window.onerror` (uncaught synchronous errors)
 *   - `window.unhandledrejection` (uncaught promise rejections)
 *
 * The buffer is read once when the user submits a bug-report — the
 * snapshot is included in the payload so the support team gets the
 * recent error trail without the user manually copying logs.
 */

export interface ConsoleErrorEntry {
  timestamp: string;
  type: 'console.error' | 'window.error' | 'unhandledrejection';
  message: string;
  stack?: string;
  source?: string;
}

const MAX_ERRORS = 50;
const buffer: ConsoleErrorEntry[] = [];
let initialized = false;

function push(entry: ConsoleErrorEntry): void {
  buffer.push(entry);
  if (buffer.length > MAX_ERRORS) {
    buffer.splice(0, buffer.length - MAX_ERRORS);
  }
}

function safeStringify(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  try {
    return JSON.stringify(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
}

export function initConsoleErrorBuffer(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  const originalError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    push({
      timestamp: new Date().toISOString(),
      type: 'console.error',
      message: args.map(safeStringify).join(' '),
    });
    originalError(...args);
  };

  window.addEventListener('error', (event) => {
    push({
      timestamp: new Date().toISOString(),
      type: 'window.error',
      message: event.message,
      stack: event.error?.stack,
      source: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    push({
      timestamp: new Date().toISOString(),
      type: 'unhandledrejection',
      message: safeStringify(reason?.message ?? reason),
      stack: reason?.stack,
    });
  });
}

export function getConsoleErrors(): ConsoleErrorEntry[] {
  return buffer.slice();
}

export function clearConsoleErrors(): void {
  buffer.length = 0;
}
